import config from '@/config'
import { getSalesforceToken } from '@/api/salesforce'

const getSalesforceTokenFromAPI = async (enabled = false) => {
  if (!enabled) {
    return ''
  }
  try {
    const { data } = await getSalesforceToken()
    return data?.data?.token || ''
  } catch (error) {
    console.error('[SF Chat] Failed to get Salesforce token', error)
    return ''
  }
}

let salesforceInitialized = false
let bootstrapPromise = null
let embeddedMessagingReady = false
let linkClickHandler = null
let maximizeHandler = null

const hasEmbeddedMessagingFrame = () => {
  return !!document.getElementById('embeddedMessagingFrame')
}

const isEmbeddedServiceReady = () => {
  if (embeddedMessagingReady) {
    return true
  }

  const bootstrap = window.embeddedservice_bootstrap
  if (bootstrap) {
    const hasRuntimeApi = !!bootstrap.utilAPI
    const hasRuntimeFrame = hasEmbeddedMessagingFrame()

    // Require both API and frame to avoid false positives after a partial teardown
    if (hasRuntimeApi && hasRuntimeFrame) {
      return true
    }
  }

  return false
}

const loadSalesforceBootstrap = (locale, prechatData) => {
  if (window.embeddedservice_bootstrap) {
    return Promise.resolve()
  }

  if (bootstrapPromise) {
    return bootstrapPromise
  }

  const authenticated = prechatData?.Authenticated_Contact === 'Yes'
  const bootstrapPlaceholderId = authenticated
    ? 'sf-bootstrap'
    : 'sf-unauthenticated-bootstrap'

  bootstrapPromise = new Promise((resolve, reject) => {
    const placeholder = document.getElementById(bootstrapPlaceholderId)

    if (!placeholder) {
      bootstrapPromise = null
      reject(
        new Error(`[SF Chat] Placeholder #${bootstrapPlaceholderId} not found`)
      )
      return
    }

    const handleLoad = () => {
      placeholder.setAttribute('data-loaded', 'true')
      placeholder.removeEventListener('load', handleLoad)
      placeholder.removeEventListener('error', handleError)
      resolve()
    }

    const handleError = (error) => {
      placeholder.removeEventListener('load', handleLoad)
      placeholder.removeEventListener('error', handleError)
      bootstrapPromise = null
      reject(
        error ||
          new Error('Error loading Salesforce Embedded Service bootstrap.')
      )
    }

    if (placeholder.getAttribute('data-loaded') === 'true') {
      resolve()
      return
    }

    placeholder.addEventListener('load', handleLoad, { once: true })
    placeholder.addEventListener('error', handleError, { once: true })

    if (!placeholder.getAttribute('src')) {
      const dataSrc = placeholder.getAttribute('data-src')

      if (!dataSrc) {
        handleError(new Error('[SF Chat] Placeholder data-src missing'))
        return
      }

      placeholder.setAttribute('src', dataSrc)
    }
  })

  return bootstrapPromise
}

const initEmbeddedMessaging = (locale, prechatData) => {
  const {
    salesforceOrgId,
    salesforceDeploymentId,
    salesforceUnauthenticatedDeploymentId,
    salesforceEmbeddedServiceUrl,
    salesforceUnauthenticatedEmbeddedServiceUrl,
    salesforceScrt2Url,
  } = config

  if (
    !salesforceOrgId ||
    !salesforceDeploymentId ||
    !salesforceUnauthenticatedDeploymentId ||
    !salesforceEmbeddedServiceUrl ||
    !salesforceUnauthenticatedEmbeddedServiceUrl ||
    !salesforceScrt2Url
  ) {
    throw new Error(
      'Salesforce configuration is incomplete. Please verify environment variables.'
    )
  }

  if (!window.embeddedservice_bootstrap) {
    throw new Error('Salesforce bootstrap script is not available.')
  }

  const language = locale === 'en_US' ? 'en' : 'ar'
  const authenticated = prechatData?.Authenticated_Contact === 'Yes'

  window.embeddedservice_bootstrap.settings.language = language
  window.embeddedservice_bootstrap.init(
    salesforceOrgId,
    authenticated
      ? salesforceDeploymentId
      : salesforceUnauthenticatedDeploymentId,
    authenticated
      ? salesforceEmbeddedServiceUrl
      : salesforceUnauthenticatedEmbeddedServiceUrl,
    {
      scrt2URL: salesforceScrt2Url,
    }
  )
}

const waitForEmbeddedService = (timeoutMs = 15000, options = {}) => {
  if (isEmbeddedServiceReady()) {
    embeddedMessagingReady = true
    return Promise.resolve()
  }

  const { prechatData } = options

  // Always rely on the SF ready event with a timeout; no polling
  return new Promise((resolve, reject) => {
    const authenticated = prechatData?.Authenticated_Contact === 'Yes'

    const setHiddenFields = () => {
      const prechatAPI =
        window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields

      if (prechatData && prechatAPI) {
        try {
          prechatAPI({ ...prechatData })
          console.warn('[SF Chat] setHiddenFields', prechatData)
        } catch (error) {
          // prechat fields failed silently
          console.warn('[SF Chat] setHiddenFields failed', error)
        }
      }
    }

    const handleReady = async () => {
      clearTimeout(timeoutId)

      if (window.embeddedservice_bootstrap?.userVerificationAPI) {
        try {
          await window.embeddedservice_bootstrap.userVerificationAPI.clearSession(
            true
          )
        } catch (error) {
          // clearSession failed silently
          console.warn('[SF Chat] clearSession failed', error)
        }
      }

      setHiddenFields()

      // Handles the Agent-Ended scenario
      window.addEventListener(
        'onEmbeddedMessagingConversationStarted',
        setHiddenFields
      )

      const token = await getSalesforceTokenFromAPI(authenticated)
      if (token && window.embeddedservice_bootstrap?.userVerificationAPI) {
        window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
          identityTokenType: 'JWT',
          identityToken: token,
        })
      }

      embeddedMessagingReady = true
      resolve()
    }

    const timeoutId = setTimeout(() => {
      window.removeEventListener('onEmbeddedMessagingReady', handleReady)
      reject(
        new Error('Salesforce Embedded Messaging did not signal readiness')
      )
    }, timeoutMs)

    window.addEventListener('onEmbeddedMessagingReady', handleReady)
  })
}

/**
 * Initialize Salesforce Embedded Service Chat
 * @param {Object} options - Configuration options
 * @param {string} options.locale - Current locale (en_US or ar_SA)
 * @param {string} options.countryCode - User's country code
 * @param {string} options.customerId - Logged-in customer id
 * @param {Function} options.onLoad - Callback after widget loads
 * @param {Function} options.onError - Callback on error
 * @param {Function} options.onMinimize - Callback when chat is minimized
 * @returns {Promise<void>}
 */
const initializeSalesforceChat = async ({
  locale,
  countryCode,
  customerId,
  prechatData,
  onLoad,
  onError,
  onMinimize,
  onLinkClick,
  onMaximize,
}) => {
  try {
    // If already initialized, prechat fields are already set correctly
    if (isEmbeddedServiceReady() && salesforceInitialized) {
      if (onLoad) {
        onLoad()
      }
      return
    }
    await loadSalesforceBootstrap(locale, prechatData)

    const readyPromise = waitForEmbeddedService(15000, {
      customerId,
      locale: locale === 'en_US' ? 'en' : 'ar',
      countryCode,
      prechatData,
    })

    if (!isEmbeddedServiceReady()) {
      initEmbeddedMessaging(locale, prechatData)
    }

    await readyPromise

    if (!isEmbeddedServiceReady()) {
      throw new Error('Salesforce Embedded Service not loaded')
    }

    if (
      window.embeddedservice_bootstrap &&
      onMinimize &&
      onLinkClick &&
      onMaximize
    ) {
      attachMessagingEventHandlers(onMinimize, onLinkClick, onMaximize)
    }

    salesforceInitialized = true
    if (onLoad) {
      onLoad()
    }
  } catch (error) {
    console.error('[SF Chat] Initialization failed', error)
    if (onError) {
      onError()
    }
  }
}

const attachMessagingEventHandlers = (onMinimize, onLinkClick, onMaximize) => {
  if (typeof window.addEventListener !== 'function') {
    return
  }

  if (onMinimize) {
    const notifyClose = () => {
      onMinimize()
    }

    const events = [
      'onEmbeddedMessagingWindowMinimized',
      'onEmbeddedMessagingConversationClosed',
      'onEmbeddedMessagingWindowClosed',
    ]

    events.forEach((eventName) => {
      window.addEventListener(eventName, notifyClose)
    })
  }

  if (onLinkClick && !linkClickHandler) {
    linkClickHandler = (event) => {
      const link =
        event?.detail?.link ||
        event?.detail?.url ||
        event?.detail?.href ||
        event?.detail?.Link
      if (!link) {
        return
      }
      try {
        onLinkClick(link)
      } catch (error) {}
    }

    window.addEventListener('onEmbeddedMessageLinkClicked', linkClickHandler)
  }

  if (onMaximize && !maximizeHandler) {
    maximizeHandler = () => {
      try {
        onMaximize()
      } catch (error) {}
    }
    window.addEventListener(
      'onEmbeddedMessagingWindowMaximized',
      maximizeHandler
    )
  }
}

/**
 * Open Salesforce chat window
 */
const openSalesforceChat = async () => {
  const utilAPI = window.embeddedservice_bootstrap?.utilAPI
  if (utilAPI && typeof utilAPI.launchChat === 'function') {
    try {
      await utilAPI.launchChat()
      return true
    } catch (error) {
      console.error('[SF Chat] Failed to launch chat', error)
    }
  }

  return false
}

/**
 * Close Salesforce chat window
 */
const closeSalesforceChat = () => {
  const utilAPI = window.embeddedservice_bootstrap?.utilAPI
  if (utilAPI) {
    if (typeof utilAPI.minimizeChat === 'function') {
      utilAPI.minimizeChat()
      return
    }
    if (typeof utilAPI.closeChat === 'function') {
      utilAPI.closeChat()
      return
    }
  }

  const iframe = document.getElementById('embeddedMessagingFrame')
  if (iframe) {
    iframe.style.display = 'none'
  }
}

/**
 * Update Salesforce chat locale
 * @param {string} locale - Current locale (en_US or ar_SA)
 */
const updateSalesforceLocale = (locale) => {
  const messagingLanguage = locale === 'en_US' ? 'en' : 'ar'

  if (
    window.embeddedservice_bootstrap &&
    window.embeddedservice_bootstrap.settings
  ) {
    window.embeddedservice_bootstrap.settings.language = messagingLanguage
  }
}

/**
 * Clean up Salesforce chat widget
 */
const detachMessagingHandlers = () => {
  if (typeof window.removeEventListener !== 'function') {
    return
  }

  if (linkClickHandler) {
    window.removeEventListener('onEmbeddedMessageLinkClicked', linkClickHandler)
    linkClickHandler = null
  }

  if (maximizeHandler) {
    window.removeEventListener(
      'onEmbeddedMessagingWindowMaximized',
      maximizeHandler
    )
    maximizeHandler = null
  }
}

/**
 * Clear Salesforce chat session using official userVerificationAPI
 * This clears the session and chat history properly when user changes
 * @returns {Promise<void>}
 */
const clearSalesforceSession = async () => {
  const userVerificationAPI =
    window.embeddedservice_bootstrap?.userVerificationAPI
  if (
    userVerificationAPI &&
    typeof userVerificationAPI.clearSession === 'function'
  ) {
    try {
      await userVerificationAPI.clearSession(true)
    } catch (error) {
      console.error(
        '[SF Chat] Failed to clear session via userVerificationAPI',
        error
      )
    }
  } else {
    // Fallback to utilAPI if userVerificationAPI is not available
    const utilAPI = window.embeddedservice_bootstrap?.utilAPI
    if (utilAPI && typeof utilAPI.clearSession === 'function') {
      try {
        utilAPI.clearSession()
      } catch (error) {
        console.error('[SF Chat] Failed to clear session via utilAPI', error)
      }
    }
  }
}

const cleanupSalesforceChat = async () => {
  // Use Salesforce's official userVerificationAPI.clearSession to properly clear the session
  await clearSalesforceSession()

  detachMessagingHandlers()

  // Reset internal state
  bootstrapPromise = null
  embeddedMessagingReady = false
  salesforceInitialized = false
}

/**
 * Get Salesforce chatbot provider with consistent interface
 * @returns {Object} Salesforce provider interface
 */
export function getSalesforceProvider() {
  return {
    async initialize({
      locale,
      countryCode,
      customerId,
      prechatData,
      onLoad,
      onError,
      onClose,
      onLinkClick,
      onMaximize,
    }) {
      await initializeSalesforceChat({
        locale,
        countryCode,
        customerId,
        prechatData,
        onLoad,
        onError,
        onMinimize: onClose,
        onLinkClick,
        onMaximize,
      })
    },

    async open() {
      await openSalesforceChat()
    },

    close() {
      closeSalesforceChat()
    },

    updateLocale(locale) {
      updateSalesforceLocale(locale)
    },

    isReady() {
      return isEmbeddedServiceReady() && salesforceInitialized
    },

    async cleanup() {
      // Fully tear down the Salesforce runtime so a new session can re-init cleanly
      // Uses userVerificationAPI.clearSession() to properly clear session and chat history
      await cleanupSalesforceChat()
    },
  }
}

export {
  initializeSalesforceChat,
  openSalesforceChat,
  closeSalesforceChat,
  updateSalesforceLocale,
  cleanupSalesforceChat,
}
